import { Link } from "react-router-dom";
import { ArrowRight, Check, Phone, Mail } from "lucide-react";
import { GoldButton, GhostButton } from "../components/Buttons";
import { MaskedLines, Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { SERVICES, IMAGES, CONTACT, VALUE_PROPS, TAX_BENEFIT_DISCLAIMER } from "../data/content";
import Seo from "../components/Seo";

const IMAGE_MAP = { individual: IMAGES.individual, w2: IMAGES.w2, gig: IMAGES.gig, multistate: IMAGES.multistate, smallbusiness: IMAGES.smallBiz, amended: IMAGES.amended };

const ServiceRow = ({ service, index }) => {
    const Icon = service.icon;
    const image = IMAGE_MAP[service.slug];
    const flip = index % 2 === 1;
    return (
        <article className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center py-14 lg:py-20 border-t border-[#E7E4DC] first:border-t-0 ${index % 2 === 0 ? "bg-white" : "bg-[#F7F5F0]"}`} data-testid={`service-row-${service.slug}`}>
            <div className={flip ? "lg:order-2" : ""}>
                <Reveal>
                    <div className="flex items-center gap-5">
                        <span className="font-display text-5xl text-[#20242B]/15">{String(index + 1).padStart(2, "0")}</span>
                        <div className="h-12 w-12 rounded-lg border border-[#C8A45D]/50 flex items-center justify-center shrink-0">
                            <Icon className="h-5 w-5 text-[#8A6D2F]" />
                        </div>
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-[#20242B] mt-6 leading-tight">{service.title}</h2>
                    <p className="text-[#8A6D2F] italic font-display text-lg mt-3">{service.tagline}</p>
                    <p className="text-[#4A4F58] leading-relaxed mt-5">{service.description}</p>
                    <ul className="mt-7 space-y-3">
                        {service.bullets.map((b) => (
                            <li key={b} className="flex items-start gap-3 text-[#4A4F58] text-sm sm:text-base">
                                <Check className="h-4 w-4 text-[#8A6D2F] mt-1 shrink-0" /> {b}
                            </li>
                        ))}
                    </ul>
                    <Link
                        to={service.slug === "amended" ? "/tax-amendment" : `/contact?service=${service.slug}`}
                        data-testid={`service-inquire-${service.slug}`}
                        className="inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.14em] text-[#8A6D2F] border-b border-[#C8A45D]/50 pb-1 mt-8 hover:border-[#C8A45D] transition-colors duration-200"
                    >
                        Ask about this service <ArrowRight className="h-4 w-4" />
                    </Link>
                </Reveal>
            </div>
            <div className={flip ? "lg:order-1" : ""}>
                {image ? (
                    <Reveal delay={0.1}>
                        <div className="frame-gold">
                            <img src={image} alt={service.title} className="w-full h-[300px] sm:h-[420px] object-cover" loading="lazy" decoding="async" data-testid={`service-image-${service.slug}`} />
                        </div>
                    </Reveal>
                ) : (
                    <Reveal delay={0.1} className="hidden lg:block">
                        <div className="relative border border-[#E7E4DC] bg-white h-[420px] flex items-center justify-center overflow-hidden">
                            <span className="font-display italic text-[10rem] leading-none text-[#20242B]/[0.06] select-none">{String(index + 1).padStart(2, "0")}</span>
                            <div className="absolute bottom-8 left-8 right-8 border-l-2 border-[#C8A45D] pl-5">
                                <p className="text-[#737985] text-sm leading-relaxed">{service.tagline}</p>
                            </div>
                        </div>
                    </Reveal>
                )}
            </div>
        </article>
    );
};

export default function Services() {
    return (
        <div data-testid="page-services">
            <Seo
                title="Tax Services for W-2, Gig & Business | Freedom Tax Filer"
                description="Individual, W-2, gig-worker, multi-state, and small-business tax preparation — see exactly how each service works, who it is for, and what is included."
                path="/services"
            />
            {/* Page hero */}
            <section className="relative pt-44 pb-16 overflow-hidden">
                <div className="absolute inset-0">
                    <img src={IMAGES.servicesHero} alt="" className="w-full h-full object-cover opacity-25" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#111318]/70 via-[#111318]/85 to-[#111318]" />
                </div>
                <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow-dark">Tax Services</p>
                        <div className="gold-rule mt-5" />
                    </Reveal>
                    <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mt-8 max-w-3xl">
                        <MaskedLines delay={0.2} lines={["Every filing situation,", <>covered with <em className="italic text-gold-gradient">care.</em></>]} />
                    </h1>
                    <Reveal delay={0.5}>
                        <p className="text-white/70 text-lg leading-relaxed mt-8 max-w-2xl">
                            From a single W-2 to a multi-state business return — here is exactly how we help, and what each
                            service includes.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Why file with us */}
            <section className="pb-20 lg:pb-24" data-testid="services-value-props">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <SectionHeading eyebrow="Why File With Us" title="Savings by the rules. Accuracy by habit." />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
                        {VALUE_PROPS.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <Reveal key={p.title} delay={i * 0.06}>
                                    <div className="h-full bg-white border border-[#E7E4DC] shadow-sm p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#C8A45D]/60 hover:shadow-md" data-testid={`services-value-card-${i}`}>
                                        <div className="h-10 w-10 rounded-lg border border-[#C8A45D]/50 flex items-center justify-center">
                                            <Icon className="h-4 w-4 text-[#8A6D2F]" />
                                        </div>
                                        <h3 className="font-display text-lg text-[#20242B] mt-5 leading-snug">{p.title}</h3>
                                        <p className="text-[#737985] text-sm leading-relaxed mt-2">{p.text}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                    <Reveal delay={0.15}>
                        <p className="text-[#737985] text-xs leading-relaxed mt-8 max-w-3xl" data-testid="services-tax-disclaimer">{TAX_BENEFIT_DISCLAIMER}</p>
                    </Reveal>
                </div>
            </section>

            {/* Service rows */}
            <section className="pb-8" data-testid="services-list">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    {SERVICES.map((s, i) => (
                        <ServiceRow key={s.slug} service={s} index={i} />
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="pb-24 lg:pb-32 px-5 sm:px-8" data-testid="services-cta">
                <Reveal>
                    <div className="mx-auto max-w-7xl relative border border-[#C8A45D]/40 bg-[#111318] rounded-2xl px-8 sm:px-14 py-16 sm:py-20 overflow-hidden shadow-xl">
                        <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-transparent via-[#C8A45D] to-transparent" />
                        <p className="eyebrow-dark">Get Answers</p>
                        <h2 className="font-display text-3xl sm:text-5xl text-white mt-5 leading-tight max-w-2xl">
                            Have Questions About <em className="italic text-gold-gradient">Your Taxes?</em>
                        </h2>
                        <p className="text-white/60 leading-relaxed mt-5 max-w-xl">
                            A notice you don't understand, a filing situation you're unsure about — reach out. A person
                            reads every message and responds with clear next steps.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 mt-10">
                            <GoldButton asChild data-testid="services-cta-email">
                                <a href={CONTACT.emailHref}>
                                    <Mail className="h-4 w-4" /> Email Our Team
                                </a>
                            </GoldButton>
                            {CONTACT.phoneHref && (
                                <GhostButton asChild data-testid="services-cta-call">
                                    <a href={CONTACT.phoneHref}>
                                        <Phone className="h-4 w-4" /> Call Us
                                    </a>
                                </GhostButton>
                            )}
                        </div>
                        <Link to="/contact" data-testid="services-cta-discuss" className="inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.14em] text-[#E4D2A3] border-b border-[#C8A45D]/50 pb-1 mt-8 hover:border-[#E4D2A3] transition-colors duration-200">
                            Or send an inquiry form <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
